import React from "react";
import PropTypes from "prop-types";
import { Card, Col, Icon, Modal, Row, message } from "antd";
import { Table } from "components";
import { Map, List } from "immutable";
import { CIBIL_DETAILS } from "constants";
import { PermissionContextManager, PermissionContextConsumer } from "hocs";
import { STATE_STATUS } from "constants/state-status";
import SaveProxy from "utilities/save-proxy";
import { UI_PERMISSIONS } from "constants";
import { CaseService } from "../../services";
import { DocumentViewer } from "../../components";
const areDatesSame = (date1, date2) => date1.isSame(date2);

const areRecordsModified = (records, newRecords) => {
    if (records.size !== newRecords.size) {
        return true;
    }
    return newRecords
        .map((_, index) =>
            areDatesSame(_.modified_date, records.get(index).modified_date)
        )
        .reduce((agg, curr) => agg || curr, false);
};

const mergeDelta = (records, delta) => {
    if (delta.size) {
        return records.map(_ => _.merge(new Map(delta.get(_.id) || {})
            .filter((__, k)=> k!=="cibil_supported_report").toJS())
            .mergeIn(["cibil_supported_report"], delta.getIn([_.id, "cibil_supported_report"]) || []));
    }
    return records;
};

class CibilReportTable extends React.Component {
    static getDerivedStateFromProps(props, state) {
        if (areRecordsModified(state.associates, props.associates)) {
            return {
                isAssociatesChanged: true,
                associates: mergeDelta(props.associates, state.delta)
            };
        }
        return null;
    }
    state = {
        associates: this.props.associates,
        delta: new Map({}),
        cibilDetails : [],
        isAssociatesChanged: false,
        selected_associate_detail: null,
    };
    CIBIL_DETAILS_COLS = CIBIL_DETAILS

    componentDidMount() {
        if(this.props.stageId == 7){
            // "New to Credit" stays visible at stage 7 — only the other
            // fields are still hidden there.
            this.CIBIL_DETAILS_COLS = CIBIL_DETAILS.filter((_,key) => !['personal_cibil_score', 'cibil_os' , 'crif_score', 'comments', 'conditions' , 'descriptions'].includes(key));
        }
        CaseService.fetchCaseCibilAllApplicants(this.props.caseId)
        .then((response) => {
            this.setState({
                cibilDetails : response.data
            })
        })
        .catch((error) => {
            message.error("Cibil Fetch Failed")
        })
        SaveProxy.attach(1, this.updateAssociates);
    }

    componentWillUnmount() {
        SaveProxy.detach(1);
    }

    shouldComponentUpdate(nextProps, nextState) {
        const { isAssociatesChanged } = nextState;
        return isAssociatesChanged;
    }

    handleSave = (record, values) => {
        this.setState(
            {
                delta: this.state.delta.set(record.id, values)
            },
            () => SaveProxy.activate(1)
        );
    };

    updateAssociates = () => {
        if (this.state.delta.size) {
            const delta = new Map(this.state.delta);
            Promise.all(
                delta.map((_, k) => {
                    let cibilRecord = {
                        ..._,
                        cibil_report: _.cibil_report && _.cibil_report.id,
                        cibil_supported_report: _.cibil_supported_report && _.cibil_supported_report.map(_ => _.id)
                    };
                    let docs = [];
                    if(_.cibil_report) {
                        docs = [
                            ...docs,
                            _.cibil_report
                        ];
                    }
                    if(_.cibil_supported_report) {
                        docs = [
                            ...docs,
                            ..._.cibil_supported_report
                        ];
                    }
                    docs && docs.length && new List(docs).toSet().toList().toArray().map(__ => {
                        this.props.attachDocument(
                            __.document && __.document.id,
                            this.props.caseId,
                            {
                                file: __.id,
                                associate: k,
                                case: this.props.caseId,
                                is_active: true,
                                company: __.company,
                                document: __.document && __.document.id
                            }
                        );
                    });
                    this.props.update(k, cibilRecord, this.props.companyId);
                })
            ).then(() => {
            });
        }
    };
    render() {
        const { status } = this.props;
        const { associates ,cibilDetails , selected_associate_detail} = this.state;
        return (
            <PermissionContextConsumer>
                {context => (
                    <Card
                        title={"CIBIL Details"}
                        className={"padless-card small-card-title"}
                        extra={
                            <Row className="d-flex">
                                {cibilDetails && cibilDetails.filter(__ => __.bureau_type == 1).length ? cibilDetails.filter(__ => __.bureau_type == 1).map((_, key) => <div onClick={() => {
                                    if (_.file_url) {
                                        this.setState({
                                            selected_associate_detail: _
                                        })
                                    } else {
                                        message.error("File Not Found")
                                    }

                                }} key={key} style={{ textAlign: "center", cursor: "pointer", display: "inline-block", margin: "0 10px 0 10px" }}>
                                    <Col span={24}><Icon type="file-pdf" theme="twoTone" style={{ fontSize: 22 }} /></Col>
                                    <Col span={24}><label style={{ fontSize: 12, textTransform: "capitalize" }}>{_.associate.name}</label></Col>
                                </div>) : null}
                                {this.props.CIBIL_EDIT && !this.props.recreditViewOnlyPermission
                                    ? [
                                        <a
                                            className="d-flex align-items-center"
                                            key={"icon"}
                                            onClick={this.updateAssociates}
                                        >
                                            {" "}
                                            Update
                                        </a>
                                    ]
                                    : null}
                            </Row>
                            
                        }
                    >
                        {selected_associate_detail && <Modal
                            visible={selected_associate_detail && selected_associate_detail.file_url}
                            footer={null}
                            destroyOnClose
                            onCancel={() => this.setState({
                                selected_associate_detail : null
                            })}
                            width={700}
                            title={`CIBIL - ${selected_associate_detail.associate ? selected_associate_detail.associate.name.toUpperCase()  : ""}`}
                        >
                            <div className="text-center">
                                <DocumentViewer
                                    width={600}
                                    height={800}
                                    url={selected_associate_detail.file_url}
                                />
                            </div>

                        </Modal>}
                        <Table
                            columns={this.CIBIL_DETAILS_COLS.map(
                                ({
                                    onCellGenerator,
                                    editable,
                                    ...restColFields
                                }) => {
                                    if (!onCellGenerator || !editable) {
                                        return { ...restColFields };
                                    }
                                    return {
                                        onCell: onCellGenerator(
                                            this.handleSave,
                                            restColFields
                                        ),
                                        ...restColFields
                                    };
                                }
                            )}
                            checkedColumns={[]}
                            data={associates && associates.map(_ => {
                                let row = _;
                                let cibil_report = this.props.cibilDocuments && (this.props.cibilDocuments
                                    .find(__ => __.associate == _.id && __.document == 51 && __.case == this.props.caseId) || {}).file;

                                let cibil_supported_report = this.props.cibilDocuments && this.props.cibilDocuments
                                    .filter(__ => __.associate == _.id && __.document == 52 && __.case == this.props.caseId)
                                    .map(_ => _.file);
                                row = row
                                    .set("cibil_report", cibil_report)
                                    .set("cibil_supported_report", new List(cibil_supported_report))
                                    .set("cibil_score", _.getIn(["case_level_data", "cibil_score"]))
                                    .set("cibil_os", _.getIn(["case_level_data", "cibil_os"]))
                                    .set("personal_cibil_score", _.getIn(["case_level_data", "personal_cibil_score"]))
                                    .set("cibil_score_range", _.getIn(["case_level_data", "cibil_score_range"]))
                                    .set("crif_score", _.getIn(["case_level_data", "crif_score"]))
                                    .set("cibil_remarks", _.getIn(["case_level_data", "cibil_remarks"]))
                                    .set("bureau", _.getIn(["case_level_data", "applicable_bureau"]))
                                    .set("is_new_to_credit", _.getIn(["case_level_data", "is_new_to_credit"]))
                                    .set("comments", _.getIn(["case_level_data", "credit_checker_remarks", "comments"]))
                                    .set("conditions", _.getIn(["case_level_data", "credit_checker_remarks", "conditions"]))
                                    .set("descriptions", _.getIn(["case_level_data", "credit_checker_remarks", "descriptions"]));
                                return row;
                            }).toList()}
                            showTitle={false}
                            showTopSection={false}
                            pagination={false}
                            keyFilter={"id"}
                            loading={
                                status !== STATE_STATUS.FETCHED &&
                                status !== STATE_STATUS.UPDATED
                            }
                            editable={context.get(
                                "credit_appraisal_cibil_edit"
                            )}
                            noRowSelection={true}
                            className={"inbox-table"}
                        />
                    </Card>
                )}
            </PermissionContextConsumer>
        );
    }
}

CibilReportTable.propTypes = {
    companyId: PropTypes.number,
    associates: PropTypes.object,
    status: PropTypes.string,
    save: PropTypes.bool,
    update: PropTypes.func,
    CIBIL_EDIT: PropTypes.bool,
    isUserAllowedToEdit: PropTypes.bool,
    caseId: PropTypes.number,
    attachDocument: PropTypes.func,
    cibilDocuments: PropTypes.array,
    recreditViewOnlyPermission: PropTypes.array,
    stageId : PropTypes.number
};

export default PermissionContextManager({
    permission: UI_PERMISSIONS.CIBIL_VIEW,
    propsPermissions: {
        CIBIL_EDIT: UI_PERMISSIONS.CIBIL_EDIT
    }
})(CibilReportTable);
